import React, { ChangeEvent, FocusEvent, ForwardedRef, useCallback } from 'react';

import excludePropsByComponent from '../../../utils/components/_excludePropsByComponent';
import Control, { ControlProps, ControlRenderProps } from '../Control';
import CheckboxItem, { CheckboxItemProps } from '../CheckboxItem';

export type CheckboxItemControlValidationProps = {
  /** Adds the `required` validator to the control. The control will be invalid if the value different from `valueChecked`. */
  required?: boolean;
};

export interface CheckboxItemControlProps<
  Value = string,
  StateValue = string,
> extends
  Omit<CheckboxItemProps, 'defaultValue' | 'name' | 'value'>,
  Omit<ControlProps<Value, StateValue, HTMLInputElement, CheckboxItemControlValidationProps>, 'children'>,
  CheckboxItemControlValidationProps {

  /** Value of the control when unchecked. */
  valueUnchecked?: Value;
  /** Value of the control when checked. */
  valueChecked?: Value;
  ref?: ForwardedRef<HTMLInputElement>;
}

interface CheckboxItemControlInternalProps<Value> extends Omit<ControlRenderProps<Value, HTMLInputElement>, 'onChange' | 'onBlur' | 'onFocus' | 'label' | 'id' | 'onKeyDown'> {}

export interface CheckboxItemControlComposedProps<
  Value = string,
  StateValue = string,
> extends CheckboxItemControlProps<Value, StateValue>, CheckboxItemControlInternalProps<Value> {}

export const CheckboxItemControlDefaultProps: Partial<CheckboxItemControlProps<any, any>> = {
  valueChecked: true,
  valueUnchecked: false,
};

const CheckboxItemControl = <
  Value = string,
  StateValue = string,
>({
    id, name, defaultValue, valueChecked, valueUnchecked, readOnly, errorMessagesId, value, isValid, isTouched, textOnly, onValueChange, onChange, onBlur, ...props
  }: CheckboxItemControlComposedProps<Value, StateValue>) => {
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    onValueChange(event.target.checked ? valueChecked : valueUnchecked);

    if (onChange) {
      onChange(event);
    }
  }, [onChange, onValueChange, valueChecked, valueUnchecked]);

  const handleBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
    onValueChange(event.target.checked ? valueChecked : valueUnchecked);

    if (onBlur) {
      onBlur(event);
    }
  }, [onBlur, onValueChange, valueChecked, valueUnchecked]);

  return (
    <CheckboxItem
      id={id}
      data-control
      data-testid="CheckboxItemControl"
      data-control-nesting="2"
      aria-invalid={Boolean(!isValid && isTouched)}
      aria-errormessage={errorMessagesId}
      readOnly={readOnly}
      textOnly={textOnly}
      {...excludePropsByComponent(props, Control)}
      checked={value === valueChecked}
      name={name}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

CheckboxItemControl.defaultProps = CheckboxItemControlDefaultProps;

export default CheckboxItemControl;
